import { SITE_ORIGIN, SITE_NAME, absoluteUrl } from "@/utils/agentDiscovery";

type BreadcrumbItem = {
  name: string;
  url: string;
};

const roaveReviews = [
  {
    name: "Kayla C",
    datePublished: "2026-01-21",
    reviewBody:
      "We had an absolutely amazing experience working with Roave Travel for our family vacation. Flights, hotel, and cruise were perfectly coordinated, and every question was answered quickly.",
  },
  {
    name: "Zannatul Tazrin",
    datePublished: "2025-02-17",
    reviewBody:
      "We had a fantastic experience with Roave Travel. They made our hotel booking process smooth and hassle-free, and the price they secured for us was unbeatable.",
  },
  {
    name: "Veronica Franco",
    datePublished: "2024-05-29",
    reviewBody:
      "I have booked multiple trips with Roave Travel and the experience is always great. Roave is reliable and I always find strong deals when booking through them.",
  },
];

function cleanText(value?: unknown) {
  if (typeof value !== "string") return undefined;
  const text = value.replace(/<\/?[^>]+(>|$)/g, " ").replace(/\s+/g, " ").trim();
  return text || undefined;
}

function asNumber(value?: unknown) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function firstImage(hotel: any) {
  const images = hotel?.hotel_images?.regular || hotel?.images?.regular || [];
  const first = Array.isArray(images) ? images[0] : null;
  return first?.url || first?.image || hotel?.image || undefined;
}

function hotelAddress(hotel: any) {
  const city = hotel?.city || hotel?.city_detail?.name;
  const country = hotel?.country || hotel?.country_detail?.name || hotel?.country_code;
  const address = hotel?.address;

  if (!address && !city && !country) return undefined;

  return {
    "@type": "PostalAddress",
    streetAddress: address,
    addressLocality: city,
    postalCode: hotel?.postal_code,
    addressCountry: country,
  };
}

function hotelGeo(hotel: any) {
  const latitude = asNumber(hotel?.geolocation?.latitude ?? hotel?.latitude);
  const longitude = asNumber(hotel?.geolocation?.longitude ?? hotel?.longitude);

  if (latitude === undefined || longitude === undefined) return undefined;

  return {
    "@type": "GeoCoordinates",
    latitude,
    longitude,
  };
}

function hotelAggregateRating(hotel: any) {
  const ratingValue = asNumber(hotel?.tripadvisor_data?.rating ?? hotel?.rating);
  const reviewCount = asNumber(hotel?.tripadvisor_data?.num_reviews ?? hotel?.numberOfReviews);

  if (!ratingValue || !reviewCount) return undefined;

  return {
    "@type": "AggregateRating",
    ratingValue,
    reviewCount,
    bestRating: 5,
    worstRating: 1,
  };
}

function hotelReview(hotel: any) {
  const ratingValue = asNumber(hotel?.tripadvisor_data?.rating);
  const reviewCount = asNumber(hotel?.tripadvisor_data?.num_reviews);

  if (!ratingValue || !reviewCount) return undefined;

  return {
    "@type": "Review",
    author: {
      "@type": "Organization",
      name: "Tripadvisor travelers",
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue,
      bestRating: 5,
      worstRating: 1,
    },
    reviewBody: `${hotel?.name || "This hotel"} has a ${ratingValue} traveler rating based on ${reviewCount} Tripadvisor reviews.`,
  };
}

function hotelOffer(hotel: any, url: string) {
  const rate = hotel?.min_rate || hotel?.rates?.[0];
  const price = asNumber(rate?.priceWithMarkup ?? rate?.price);
  const currency = rate?.currency || hotel?.currency || "USD";

  return {
    "@type": "Offer",
    url,
    price,
    priceCurrency: currency,
    availability: "https://schema.org/InStock",
    businessFunction: "https://schema.org/Sell",
    itemOffered: {
      "@type": "HotelRoom",
      name: rate?.room_name || rate?.rooms?.[0]?.description || "Hotel room",
    },
    seller: {
      "@id": `${SITE_ORIGIN}/#travelagency`,
    },
  };
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.url),
    })),
  };
}

export function buildTravelAgencySchema() {
  return {
    "@type": "TravelAgency",
    "@id": `${SITE_ORIGIN}/#travelagency`,
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: absoluteUrl("/img/general/logo-dark.svg"),
    image: absoluteUrl("/img/general/logo-dark.svg"),
    description:
      "Roave Travel helps travelers book hotels and trips with clear pricing, direct support, and curated travel planning.",
    telephone: process.env.NEXT_PUBLIC_ADMIN_CONTACT,
    email: process.env.NEXT_PUBLIC_ADMIN_EMAIL,
    address: {
      "@type": "PostalAddress",
      streetAddress: "5432 Geary Blvd Unit 787",
      addressLocality: "San Francisco",
      addressRegion: "CA",
      postalCode: "94121",
      addressCountry: "US",
    },
    areaServed: [
      { "@type": "Country", name: "United States" },
      { "@type": "Country", name: "Canada" },
      { "@type": "Country", name: "India" },
    ],
    sameAs: [
      process.env.NEXT_PUBLIC_LINKEDIN,
      process.env.NEXT_PUBLIC_INSTAGRAM,
      process.env.NEXT_PUBLIC_FACEBOOK,
    ].filter(Boolean),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: 5,
      reviewCount: roaveReviews.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: roaveReviews.map((review) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: review.name,
      },
      datePublished: review.datePublished,
      reviewRating: {
        "@type": "Rating",
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: review.reviewBody,
    })),
  };
}

export function buildSiteSchemaGraph(_faqItems: any[] = []) {
  const graph: any[] = [
    buildTravelAgencySchema(),
    {
      "@type": "WebSite",
      "@id": `${SITE_ORIGIN}/#website`,
      name: SITE_NAME,
      url: SITE_ORIGIN,
      publisher: {
        "@id": `${SITE_ORIGIN}/#travelagency`,
      },
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_ORIGIN}/hotels?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function buildHotelSchema(hotel: any, url: string) {
  const name = hotel?.name || "Roave hotel";
  const image = firstImage(hotel);
  const description =
    cleanText(hotel?.description) ||
    `Review ${name} details, rooms, amenities, prices, and availability with Roave Travel.`;
  const aggregateRating = hotelAggregateRating(hotel);
  const review = hotelReview(hotel);

  return {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "@id": `${url}#hotel`,
    name,
    url,
    description,
    image: image ? [image] : undefined,
    address: hotelAddress(hotel),
    geo: hotelGeo(hotel),
    starRating: hotel?.category
      ? {
          "@type": "Rating",
          ratingValue: asNumber(hotel.category),
        }
      : undefined,
    aggregateRating,
    review,
    makesOffer: hotelOffer(hotel, url),
  };
}

export function buildHotelFallbackSchema({
  city,
  hotelCode,
}: {
  city: string;
  hotelCode: string;
}) {
  const hotelName = hotelCode.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  const cityName = city.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  const url = absoluteUrl(`/hotels/${city}/${hotelCode}`);

  return buildHotelSchema(
    {
      name: hotelName,
      city: cityName,
      description: `${hotelName} hotel details, room options, amenities, and booking availability in ${cityName}.`,
      rates: [
        {
          currency: "USD",
          room_name: "Hotel room",
        },
      ],
    },
    url
  );
}
